import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import type { QuickLead, QuickLeadRepId } from "../leads/quickLead";

const convertTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (typeof timestamp === "string") return new Date(timestamp);
  return null;
};

const serializeDate = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();

const parseRepId = (v: unknown): QuickLeadRepId => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") return v;
  return null;
};

const trimOrNull = (v: unknown): string | null => {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t ? t : null;
};

const parseStringArray = (v: unknown): string[] => {
  if (!Array.isArray(v)) return [];
  return v
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (typeof item === "number" && Number.isFinite(item)) return String(item);
      return "";
    })
    .filter(Boolean);
};

export const quickLeadConverter: FirestoreDataConverter<QuickLead> = {
  toFirestore(row: QuickLead) {
    const { id: _id, ...rest } = row;
    return {
      firstName: rest.firstName?.trim() ?? "",
      lastName: rest.lastName?.trim() ?? "",
      company: rest.company?.trim() ?? "",
      wantSell: rest.wantSell === true,
      wantBuy: rest.wantBuy === true,
      terminalId: rest.terminalId?.trim() ? rest.terminalId.trim() : null,
      terminalText: rest.terminalText?.trim() ? rest.terminalText.trim() : null,
      equipmentTypes: parseStringArray(rest.equipmentTypes),
      equipmentSizes: parseStringArray(rest.equipmentSizes),
      equipmentYears: parseStringArray(rest.equipmentYears),
      budget: rest.budget?.trim() ? rest.budget.trim() : null,
      preferences: rest.preferences?.trim() ? rest.preferences.trim() : null,
      notes: rest.notes?.trim() ? rest.notes.trim() : null,
      salesRepId: rest.salesRepId === undefined ? null : rest.salesRepId,
      inventoryRepId: rest.inventoryRepId === undefined ? null : rest.inventoryRepId,
      createdAt: serializeDate(rest.createdAt),
      updatedAt: serializeDate(rest.updatedAt),
      createdByUserId: rest.createdByUserId ?? "",
    };
  },

  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): QuickLead {
    const data = snapshot.data(options);
    return {
      id: snapshot.id,
      firstName: typeof data?.firstName === "string" ? data.firstName : "",
      lastName: typeof data?.lastName === "string" ? data.lastName : "",
      company: typeof data?.company === "string" ? data.company : "",
      wantSell: data?.wantSell === true,
      wantBuy: data?.wantBuy === true,
      terminalId: trimOrNull(data?.terminalId),
      terminalText: trimOrNull(data?.terminalText),
      equipmentTypes: parseStringArray(data?.equipmentTypes),
      equipmentSizes: parseStringArray(data?.equipmentSizes),
      equipmentYears: parseStringArray(data?.equipmentYears),
      budget: trimOrNull(data?.budget),
      preferences: trimOrNull(data?.preferences),
      notes: trimOrNull(data?.notes),
      salesRepId: parseRepId(data?.salesRepId),
      inventoryRepId: parseRepId(data?.inventoryRepId),
      createdAt: convertTimestamp(data?.createdAt) || new Date(),
      updatedAt: convertTimestamp(data?.updatedAt) || new Date(),
      createdByUserId: typeof data?.createdByUserId === "string" ? data.createdByUserId : "",
    };
  },
};
